import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import {
  Box, Grid, Typography, Paper, Stack, Button, Chip,
  Card, CardContent, useTheme, CircularProgress,
  LinearProgress, Avatar, alpha, Divider, Tooltip
} from "@mui/material";
import {
  CheckCircleRounded,
  History as HistoryIcon,
  ShieldOutlined,
  VerifiedUserOutlined,
  LayersOutlined,
  TimelineOutlined,
  AutoGraphOutlined,
  Bolt,
  SettingsInputComponent,
  Storage,
  CloudQueue,
  Security,
  RocketLaunchOutlined,
  MemoryOutlined,
  TrendingUpOutlined,
  VerifiedOutlined
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { initiatePayment } from "../../store/slices/subscriptionSlice";
import { getPartnerProfile } from "../../store/slices/partnerSlice";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import ModernLoader from "../../components/common/ModernLoader";

const PartnerSubscription = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user: clerkUser } = useUser();
  const { partner, loading } = useSelector(state => state.partner);
  const isDark = theme.palette.mode === 'dark';

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processing, setProcessing] = useState(false);
  const processedStatusRef = React.useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status && processedStatusRef.current !== status) {
      processedStatusRef.current = status;
      if (status === 'success' && clerkUser) {
        dispatch(getPartnerProfile(clerkUser.id));
        toast.success("Security Handshake Authenticated");
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [location.search, dispatch, clerkUser]);

  const plans = [
    {
      name: 'Starter', price: 0, color: '#94a3b8', icon: <LayersOutlined />,
      features: ['1 Operational Center', '3 Daily Broadcasts', '8% Network Commission', 'Standard Settlement'],
      desc: 'Local theater nodes with baseline synchronization.'
    },
    {
      name: 'Growth', price: 4999, color: '#6366f1', popular: true, icon: <TimelineOutlined />,
      features: ['2 Operational Centers', 'Unlimited Broadcasts', '4% Network Commission', 'T+2 Accelerated Cycle', 'Core Analytics Hub'],
      desc: 'Scaling enterprise nodes with high-throughput features.'
    },
    {
      name: 'Pro', price: 14999, color: '#8b5cf6', icon: <AutoGraphOutlined />,
      features: ['5 Operational Centers', 'Zero Threshold Limits', '2% Network Commission', 'Priority Neural Sync', 'Dedicated Liaison'],
      desc: 'Market leaders requiring massive infrastructure scale.'
    }
  ];

  const handleSubscribe = async (planName) => {
    if (planName === 'Starter') return;
    setProcessing(true);
    try {
      const result = await dispatch(initiatePayment({ userId: clerkUser.id, planName, billingCycle })).unwrap();
      if (result.paymentData) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = result.paymentData.action;
        Object.entries(result.paymentData).forEach(([k, v]) => {
          if (k !== 'action') {
            const input = document.createElement('input');
            input.type = 'hidden'; input.name = k; input.value = v;
            form.appendChild(input);
          }
        });
        document.body.appendChild(form);
        form.submit();
      }
    } catch (err) {
      toast.error("Encryption Overload: Try Again");
      setProcessing(false);
    }
  };

  const currentPlan = partner?.currentPlan || 'Starter';
  const currentPlanObj = plans.find(p => p.name === currentPlan);

  if (loading && !partner) return <ModernLoader text="Syncing Neural Network..." />;

  return (
    <Box sx={{
      p: { xs: 2, md: 4 }, minHeight: '100%',
      bgcolor: 'transparent', position: 'relative', overflow: 'hidden'
    }}>
      {/* Background Complexity Layer */}
      <Box sx={{ position: 'absolute', top: -300, right: -200, width: 600, height: 600, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: -200, left: -200, width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${alpha('#8b5cf6', 0.05)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

      <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
        {/* LEFT COLUMN: TELEMETRY (Machine Pattern) */}
        <Grid item xs={12} lg={4} xl={3.2}>
          <Stack spacing={4}>
            <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }}>
              <Paper sx={{
                p: 0, borderRadius: '32px', overflow: 'hidden',
                bgcolor: isDark ? alpha('#0b0b0e', 0.6) : '#fff',
                border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                backdropFilter: 'blur(30px)',
                boxShadow: isDark ? '0 40px 100px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.06)'
              }}>
                {/* HUD Header */}
                <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.05)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <MemoryOutlined sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 950, letterSpacing: '0.2em', opacity: 0.6 }}>INFRA_HUD_V2</Typography>
                  </Stack>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', animation: 'blink 1.5s infinite' }} />
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: alpha('#10b981', 0.3) }} />
                  </Box>
                </Box>

                {/* Active Plan Node */}
                <Box sx={{ p: 5, textAlign: 'center', position: 'relative' }}>
                  <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} transition={{ duration: 1, repeat: Infinity, repeatType: 'reverse' }} style={{ position: 'absolute', top: 30, right: 30, opacity: 0.03 }}>
                    <TrendingUpOutlined sx={{ fontSize: 120 }} />
                  </motion.div>
                  <Avatar sx={{
                    width: 96, height: 96, mx: 'auto', mb: 3, borderRadius: '28px',
                    bgcolor: alpha(currentPlanObj?.color || '#3b82f6', 0.1),
                    color: currentPlanObj?.color || '#3b82f6',
                    boxShadow: `0 20px 40px ${alpha(currentPlanObj?.color || '#3b82f6', 0.2)}`,
                    border: `1px solid ${alpha(currentPlanObj?.color || '#3b82f6', 0.2)}`
                  }}>
                    {React.cloneElement(currentPlanObj?.icon || <Bolt />, { fontSize: 'large' })}
                  </Avatar>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 950, letterSpacing: '-0.03em' }}>{currentPlan}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, opacity: 0.4, textTransform: 'uppercase', mt: 0.5, letterSpacing: '0.05em' }}>Operational Deployment Phase</Typography>
                </Box>

                <Box sx={{ p: 4, pt: 0 }}>
                  <Stack spacing={3.5}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>Node Saturation</Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 950, color: theme.palette.primary.main }}>40% UTILIZED</Typography>
                        </Box>
                      </Stack>
                      <LinearProgress variant="determinate" value={40} sx={{ height: 10, borderRadius: 5, bgcolor: alpha(theme.palette.text.primary, 0.05), '& .MuiLinearProgress-bar': { borderRadius: 5, background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.6)})` } }} />
                    </Box>

                    <Divider sx={{ opacity: 0.05 }} />

                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, opacity: 0.4, mb: 1, letterSpacing: '0.1em' }}>LEDGER_COMMISSION</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <VerifiedOutlined sx={{ fontSize: 16, color: '#10b981' }} />
                          <Typography sx={{ fontSize: '1rem', fontWeight: 950, color: '#10b981' }}>2.0%</Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, opacity: 0.4, mb: 1, letterSpacing: '0.1em' }}>UPTIME_PULSE</Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 950 }}>99.98%</Typography>
                      </Grid>
                    </Grid>

                    <Button fullWidth variant="outlined" startIcon={<HistoryIcon />} sx={{ py: 2, borderRadius: '16px', fontWeight: 950, textTransform: 'none', border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`, '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.02), border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}` } }}>
                      Access Settlement Ledger
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </motion.div>

            <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.1 }}>
              <Paper sx={{
                p: 4, borderRadius: '32px',
                bgcolor: isDark ? alpha('#0b0b0e', 0.6) : '#fff',
                border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                backdropFilter: 'blur(30px)'
              }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 950, opacity: 0.5, letterSpacing: '0.2em', mb: 4 }}>COMPLIANCE_ENFORCEMENT</Typography>
                <Stack spacing={3.5}>
                  {[
                    { icon: ShieldOutlined, label: "PCI-DSS Level 1 Encryption" },
                    { icon: VerifiedUserOutlined, label: "Biometric Identity Shield" },
                    { icon: Security, label: "Quantum Handshake Protocol" }
                  ].map((m, i) => (
                    <Stack key={i} direction="row" spacing={2.5} alignItems="center">
                      <Box sx={{ p: 1, borderRadius: '10px', bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                        <m.icon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>{m.label}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </motion.div>
          </Stack>
        </Grid>

        {/* RIGHT COLUMN: CONFIGURATION ENGINE */}
        <Grid item xs={12} lg={8} xl={8.8}>
          <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', px: 1 }}>
            <Box>
              <Typography sx={{ fontSize: '2.85rem', fontWeight: 950, letterSpacing: '-0.05em', lineHeight: 1.1 }}>Configuration Engine</Typography>
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, opacity: 0.5, mt: 1 }}>Choose your operational scale on the District network.</Typography>
            </Box>
            <Paper elevation={0} sx={{ p: 0.8, borderRadius: '18px', bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.05), display: 'flex', gap: 0.5, backdropFilter: 'blur(10px)' }}>
              <Button size="medium" onClick={() => setBillingCycle('monthly')} sx={{ px: 4, borderRadius: '14px', fontWeight: 950, bgcolor: billingCycle === 'monthly' ? (isDark ? '#232326' : '#fff') : 'transparent', color: billingCycle === 'monthly' ? theme.palette.primary.main : theme.palette.text.secondary }}>Monthly Cycle</Button>
              <Button size="medium" onClick={() => setBillingCycle('yearly')} sx={{ px: 4, borderRadius: '14px', fontWeight: 950, bgcolor: billingCycle === 'yearly' ? (isDark ? '#232326' : '#fff') : 'transparent', color: billingCycle === 'yearly' ? theme.palette.primary.main : theme.palette.text.secondary }}>
                Yearly Cycle <Chip label="-20%" size="small" sx={{ ml: 1.5, height: 20, fontSize: '0.65rem', fontWeight: 950, bgcolor: '#10b981', color: '#fff' }} />
              </Button>
            </Paper>
          </Box>

          <Grid container spacing={4}>
            {plans.map((plan, i) => (
              <Grid item xs={12} md={4} key={plan.name}>
                <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: i * 0.1 }}>
                  <Card sx={{
                    borderRadius: '36px', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'visible',
                    border: plan.popular ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                    bgcolor: isDark ? alpha('#0b0b0e', 0.6) : '#fff',
                    backdropFilter: 'blur(30px)',
                    boxShadow: plan.popular ? `0 40px 100px ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
                    '&:hover': { transform: 'translateY(-12px)', transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }
                  }}>
                    {plan.popular && (
                      <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', bgcolor: theme.palette.primary.main, color: '#fff', px: 3, py: 0.8, borderRadius: '50px', fontSize: '0.65rem', fontWeight: 950, letterSpacing: '0.15em', boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.4)}` }}>PEAK_EFFICIENCY</Box>
                    )}
                    <CardContent sx={{ p: 5, flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                        <Avatar sx={{ bgcolor: alpha(plan.color, 0.1), color: plan.color, borderRadius: '20px', width: 56, height: 56, border: `1px solid ${alpha(plan.color, 0.2)}` }}>{plan.icon}</Avatar>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, opacity: 0.4 }}>COMMISSION</Typography>
                          <Typography sx={{ fontSize: '1.2rem', fontWeight: 950, color: plan.price > 0 ? theme.palette.text.primary : '#94a3b8' }}>{plan.features[2].split(' ')[0]}</Typography>
                        </Box>
                      </Box>

                      <Typography sx={{ fontSize: '1.75rem', fontWeight: 950, mb: 1, letterSpacing: '-0.02em' }}>{plan.name}</Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, opacity: 0.5, mb: 5, minHeight: 48 }}>{plan.desc}</Typography>

                      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 6 }}>
                        <Typography sx={{ fontSize: '3rem', fontWeight: 950, letterSpacing: '-0.04em' }}>₹{billingCycle === 'yearly' ? Math.floor(plan.price * 12 * 0.8 / 12).toLocaleString() : plan.price.toLocaleString()}</Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 800, opacity: 0.3, ml: 1.5 }}>/ MO</Typography>
                      </Box>

                      <Stack spacing={2.5}>
                        {plan.features.map((f, idx) => (
                          <Stack direction="row" spacing={2} key={idx} alignItems="flex-start">
                            <CheckCircleRounded sx={{ color: plan.popular ? theme.palette.primary.main : '#10b981', fontSize: 20, mt: 0.2 }} />
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, opacity: 0.9 }}>{f}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </CardContent>

                    <Box sx={{ p: 5, pt: 0 }}>
                      <Button
                        fullWidth variant={plan.popular ? "contained" : "outlined"}
                        disabled={currentPlan === plan.name || processing}
                        onClick={() => handleSubscribe(plan.name)}
                        sx={{ py: 2.2, borderRadius: '20px', fontWeight: 950, textTransform: 'none', fontSize: '1.05rem', boxShadow: plan.popular ? `0 15px 30px ${alpha(theme.palette.primary.main, 0.3)}` : 'none' }}
                      >
                        {processing ? <CircularProgress size={24} color="inherit" /> : (currentPlan === plan.name ? 'Phase Active' : 'Deploy Operational Node')}
                      </Button>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Network Sync Dashboard Footer */}
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
            <Paper sx={{
              mt: 6, p: 4, borderRadius: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              bgcolor: isDark ? alpha('#0b0b0e', 0.4) : alpha('#000', 0.02), border: `1px dashed ${alpha(theme.palette.text.primary, 0.1)}`,
              backdropFilter: 'blur(20px)'
            }}>
              <Stack direction="row" spacing={6}>
                {[
                  { icon: SettingsInputComponent, label: "Neural Routing", val: "ACTIVE" },
                  { icon: Storage, label: "Grid Latency", val: "14.2ms" },
                  { icon: CloudQueue, label: "State Consensus", val: "STABLE" }
                ].map((stat, i) => (
                  <Stack key={i} direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, borderRadius: '12px' }}>
                      <stat.icon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, opacity: 0.4, letterSpacing: '0.05em' }}>{stat.label.toUpperCase()}</Typography>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 950 }}>{stat.val}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 950, opacity: 0.2, letterSpacing: '0.3em', textAlign: 'right' }}>
                DISTRICT_SYNC_ENG_V4.0 // STABLE_CHANNEL
              </Typography>
            </Paper>
          </motion.div>
        </Grid>
      </Grid>

      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </Box>
  );
};

export default PartnerSubscription;
